// Eine der drei Zonen des Editors: Nummer, Titel, ein Satz, Inhalt.
//
// `zu`: nur der Kopf steht da (ab lg; auf dem Telefon sind die Zonen
// Schritte und bleiben offen). `fuellen`: die Karte nimmt die ganze
// Hoehe ihrer Rasterzeile — Inhalt und Vorschau enden auf einer Linie.
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export function Zone({
  nummer,
  titel,
  beschreibung,
  rechts,
  children,
  className,
  inhaltClassName,
  id,
  zu = false,
  fuellen = false,
}: {
  nummer: number;
  titel: string;
  beschreibung?: React.ReactNode;
  rechts?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  inhaltClassName?: string;
  id?: string;
  zu?: boolean;
  fuellen?: boolean;
}) {
  return (
    <Card id={id} className={cn("scroll-mt-28", fuellen && "flex h-full flex-col", className)}>
      <div className={cn("flex items-start gap-3 px-5 pt-5", zu && "lg:pb-5")}>
        <span className="bg-bg-tertiary text-caption text-text-secondary grid h-6 w-6 shrink-0 place-items-center rounded-full font-semibold tabular-nums">
          {nummer}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-heading-sm text-text-primary">{titel}</h2>
          {beschreibung && (
            <div className="text-body-sm text-text-secondary mt-0.5">{beschreibung}</div>
          )}
        </div>
        {rechts && <div className="shrink-0">{rechts}</div>}
      </div>
      <CardContent
        className={cn(
          "space-y-5 pt-5",
          fuellen && "flex flex-1 flex-col",
          zu && "lg:hidden",
          inhaltClassName,
        )}
      >
        {children}
      </CardContent>
    </Card>
  );
}

/** Eine Angabe in der Zone: Beschriftung, Feld, leiser Hinweis. */
export function Angabe({
  titel,
  hinweis,
  fehler,
  children,
  htmlFor,
  rechts,
  className,
}: {
  titel: string;
  hinweis?: React.ReactNode;
  fehler?: string;
  children: React.ReactNode;
  htmlFor?: string;
  rechts?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={htmlFor} className="text-overline text-text-tertiary">
          {titel}
        </label>
        {rechts}
      </div>
      {children}
      {fehler ? (
        <p className="text-caption text-danger" role="alert">
          {fehler}
        </p>
      ) : hinweis ? (
        <div className="text-caption text-text-tertiary">{hinweis}</div>
      ) : null}
    </div>
  );
}

const HINWEIS_TON = {
  neutral: "border-border bg-bg-secondary",
  info: "border-accent-2/25 bg-accent-2-subtle",
  warning: "border-warning/25 bg-warning-subtle",
  danger: "border-danger/25 bg-danger-subtle",
  success: "border-success/25 bg-success-subtle",
} as const;

/** Ein Kasten mit einer Auskunft — Datenschutz, Platzhalter, Versand. */
export function Hinweis({
  ton = "neutral",
  icon,
  children,
  aktion,
  className,
}: {
  ton?: keyof typeof HINWEIS_TON;
  icon?: React.ReactNode;
  children: React.ReactNode;
  aktion?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      role={ton === "danger" ? "alert" : undefined}
      className={cn(
        "text-body-sm text-text-primary flex items-start gap-2.5 rounded-lg border px-3 py-2.5",
        HINWEIS_TON[ton],
        className,
      )}
    >
      {icon && <span className="mt-0.5 flex shrink-0">{icon}</span>}
      <div className="min-w-0 flex-1">{children}</div>
      {aktion && <div className="shrink-0">{aktion}</div>}
    </div>
  );
}
